import create from "zustand";

const SYNC_TIMEOUTS_KEY = "cloudSyncTimeouts";

// Helper to get stored timeouts from localStorage
const getStoredTimeouts = () => {
  try {
    const stored = localStorage.getItem(SYNC_TIMEOUTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading sync timeouts from localStorage:", error);
    return {};
  }
};

// Helper to save timeouts to localStorage
const saveTimeoutsToStorage = (timeouts) => {
  try {
    localStorage.setItem(SYNC_TIMEOUTS_KEY, JSON.stringify(timeouts));
  } catch (error) {
    console.error("Error saving sync timeouts to localStorage:", error);
  }
};

// Initialize state from localStorage
const initializeState = () => {
  const storedTimeouts = getStoredTimeouts();
  const now = Date.now();
  const syncStatus = { aws: false, azure: false, gcp: false };
  const syncTimeouts = { aws: null, azure: null, gcp: null };

  Object.keys(storedTimeouts).forEach((cloud) => {
    const expiry = storedTimeouts[cloud];
    if (expiry && now < expiry) {
      syncStatus[cloud] = true;
      // Set a new timeout for the remaining time
      const remainingTime = expiry - now;
      syncTimeouts[cloud] = setTimeout(() => {
        const state = useCloudSyncStore.getState();
        state.setSyncStatus(cloud, false);
        state.setSyncTimeout(cloud, null);
        // Remove from localStorage
        const updatedTimeouts = getStoredTimeouts();
        delete updatedTimeouts[cloud];
        saveTimeoutsToStorage(updatedTimeouts);
      }, remainingTime);
    } else {
      // Expired, clean up
      const updatedTimeouts = getStoredTimeouts();
      delete updatedTimeouts[cloud];
      saveTimeoutsToStorage(updatedTimeouts);
    }
  });

  return { syncStatus, syncTimeouts };
};

const useCloudSyncStore = create((set, get) => ({
  ...initializeState(),

  setSyncStatus: (cloud, status) =>
    set((state) => ({
      syncStatus: { ...state.syncStatus, [cloud]: status },
    })),

  setSyncTimeout: (cloud, timeoutId, expiryTime = null) => {
    set((state) => ({
      syncTimeouts: { ...state.syncTimeouts, [cloud]: timeoutId },
    }));
    // Save to localStorage if expiryTime provided
    if (expiryTime !== null) {
      const storedTimeouts = getStoredTimeouts();
      storedTimeouts[cloud] = expiryTime;
      saveTimeoutsToStorage(storedTimeouts);
    }
  },

  clearSyncTimeout: (cloud) => {
    const state = get();
    if (state.syncTimeouts[cloud]) {
      clearTimeout(state.syncTimeouts[cloud]);
      set((state) => ({
        syncTimeouts: { ...state.syncTimeouts, [cloud]: null },
      }));
    }
    // Remove from localStorage
    const storedTimeouts = getStoredTimeouts();
    delete storedTimeouts[cloud];
    saveTimeoutsToStorage(storedTimeouts);
  },

  resetSyncStates: () =>
    set(() => {
      // Clear all timeouts
      const state = get();
      Object.keys(state.syncTimeouts).forEach((cloud) => {
        if (state.syncTimeouts[cloud]) {
          clearTimeout(state.syncTimeouts[cloud]);
        }
      });
      // Clear localStorage
      localStorage.removeItem(SYNC_TIMEOUTS_KEY);
      return {
        syncStatus: {
          aws: false,
          azure: false,
          gcp: false,
        },
        syncTimeouts: {
          aws: null,
          azure: null,
          gcp: null,
        },
      };
    }),
}));

export default useCloudSyncStore;
